import {
  type CSSProperties,
  type FC,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type UIEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { Dropdown } from 'antd'
import type { DropdownProps, MenuProps } from 'antd'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import cn from 'classnames'
import classes from './index.module.css'

const DEFAULT_ROOT_ATTR = 'data-cascading-dropdown-root'
const PANEL_GAP_PX = 4
const VIEWPORT_PADDING_PX = 8
/** Запас под min-width панели, пока нет реального measure. */
const ESTIMATED_PANEL_WIDTH_PX = 200

export type CascadingMenuItem = {
  key: string
  label: ReactNode
  disabled?: boolean
  children?: CascadingMenuItem[]
  onClick?: (domEvent: Event) => void
}

export type CascadingDropdownProps = {
  /** Ant Design menu items или уже нормализованное дерево. */
  items: MenuProps['items'] | CascadingMenuItem[]
  children: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  placement?: DropdownProps['placement']
  trigger?: DropdownProps['trigger']
  disabled?: boolean
  destroyPopupOnHide?: boolean
  overlayClassName?: string
  rootDataAttr?: string
  /** Задержка закрытия вложенных при уходе курсора (мс). */
  submenuLeaveDelayMs?: number
  /** Заголовки панелей по уровню (0 = корень). */
  panelTitles?: Array<ReactNode | null | undefined>
  /** Контент над списком только в корневой панели (поиск и т.п.). */
  rootHeader?: ReactNode
  /** Контент внизу скроллируемого корневого списка (прелодер infinity). */
  rootListFooter?: ReactNode
  /** Скролл списка корневой панели (infinity scroll). */
  onRootScroll?: (event: UIEvent<HTMLElement>) => void
  /** max-height скроллируемого списка корня (CSS). */
  rootListMaxHeight?: string | number
  /** Текст / нода, если корневой список пуст. */
  rootEmptyContent?: ReactNode
}

const RightIcon = RightOutlined as unknown as FC<{ className?: string }>
const LeftIcon = LeftOutlined as unknown as FC<{ className?: string }>

type CascadeSide = 'left' | 'right'

const resolveCascadeSide = (
  anchor: DOMRect,
  panelWidth: number,
): CascadeSide => {
  const spaceRight = window.innerWidth - anchor.right - PANEL_GAP_PX
  const spaceLeft = anchor.left - PANEL_GAP_PX
  if (spaceRight >= panelWidth) {
    return 'right'
  }
  if (spaceLeft >= panelWidth) {
    return 'left'
  }
  return spaceRight >= spaceLeft ? 'right' : 'left'
}

const resolvePanelPosition = (
  anchor: DOMRect,
  panelWidth: number,
  panelHeight: number,
): { top: number; left: number; side: CascadeSide } => {
  const side = resolveCascadeSide(anchor, panelWidth)
  let left =
    side === 'right'
      ? anchor.right + PANEL_GAP_PX
      : anchor.left - panelWidth - PANEL_GAP_PX
  let top = anchor.top

  const maxLeft = window.innerWidth - panelWidth - VIEWPORT_PADDING_PX
  const maxTop = window.innerHeight - panelHeight - VIEWPORT_PADDING_PX
  left = Math.min(Math.max(VIEWPORT_PADDING_PX, left), Math.max(VIEWPORT_PADDING_PX, maxLeft))
  top = Math.min(Math.max(VIEWPORT_PADDING_PX, top), Math.max(VIEWPORT_PADDING_PX, maxTop))

  return { top, left, side }
}

/** MenuProps['items'] → дерево без divider/group. */
export const normalizeCascadingMenuItems = (
  items: MenuProps['items'] | CascadingMenuItem[] | undefined,
): CascadingMenuItem[] => {
  if (!items?.length) {
    return []
  }

  const result: CascadingMenuItem[] = []

  items.forEach((raw) => {
    if (!raw || typeof raw !== 'object') {
      return
    }
    if ('type' in raw && (raw.type === 'divider' || raw.type === 'group')) {
      return
    }
    if (!('key' in raw) || raw.key == null) {
      return
    }

    const key = String(raw.key)
    const antChildren =
      'children' in raw && Array.isArray(raw.children)
        ? normalizeCascadingMenuItems(raw.children as MenuProps['items'])
        : undefined

    const antOnClick =
      'onClick' in raw && typeof raw.onClick === 'function'
        ? (raw.onClick as unknown as (info: {
            key: string
            domEvent: Event
          }) => void)
        : undefined

    result.push({
      key,
      label: 'label' in raw ? (raw.label as ReactNode) : key,
      disabled: Boolean('disabled' in raw && raw.disabled),
      children: antChildren?.length ? antChildren : undefined,
      onClick: antOnClick
        ? (domEvent) => antOnClick({ key, domEvent })
        : undefined,
    })
  })

  return result
}

type PanelProps = {
  instanceId: string
  level: number
  items: CascadingMenuItem[]
  path: string[]
  anchorRect: DOMRect | null
  /** Сторона, с которой открыт этот nested-попап (для стрелок у children). */
  cascadeSide?: CascadeSide
  rootDataAttr: string
  overlayClassName?: string
  panelTitle?: ReactNode | null
  rootHeader?: ReactNode
  rootListFooter?: ReactNode
  onRootScroll?: (event: UIEvent<HTMLElement>) => void
  rootListMaxHeight?: string | number
  rootEmptyContent?: ReactNode
  onHoverItem: (level: number, itemKey: string, hasChildren: boolean) => void
  onLeavePanel: (level: number) => void
  onEnterPanel: () => void
  onPickLeaf: (item: CascadingMenuItem, domEvent: Event) => void
  onPlacementResolved?: (level: number, side: CascadeSide) => void
}

const CascadingPanel: FC<PanelProps> = ({
  instanceId,
  level,
  items,
  path,
  anchorRect,
  cascadeSide = 'right',
  rootDataAttr,
  overlayClassName,
  panelTitle,
  rootHeader,
  rootListFooter,
  onRootScroll,
  rootListMaxHeight,
  rootEmptyContent,
  onHoverItem,
  onLeavePanel,
  onEnterPanel,
  onPickLeaf,
  onPlacementResolved,
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [coords, setCoords] = useState<{
    top: number
    left: number
    side: CascadeSide
  } | null>(null)
  const activeKey = path[level]
  const ArrowIcon = cascadeSide === 'left' ? LeftIcon : RightIcon

  useLayoutEffect(() => {
    if (!panelRef.current) {
      return
    }

    if (level === 0) {
      // Пока нет nested — стрелки корня по месту панели.
      // Когда nested открыт, сторону родителя задаёт onPlacementResolved nested-панели.
      if (path.length > 0) {
        return
      }
      const rect = panelRef.current.getBoundingClientRect()
      const side = resolveCascadeSide(rect, ESTIMATED_PANEL_WIDTH_PX)
      onPlacementResolved?.(0, side)
      setCoords(null)
      return
    }

    if (!anchorRect) {
      setCoords(null)
      return
    }

    const panel = panelRef.current
    const width = Math.max(panel.offsetWidth, ESTIMATED_PANEL_WIDTH_PX)
    const height = Math.max(panel.offsetHeight, 40)
    const next = resolvePanelPosition(anchorRect, width, height)
    setCoords(next)
    onPlacementResolved?.(level, next.side)
  }, [anchorRect, items.length, level, onPlacementResolved, path.length])

  const style: CSSProperties | undefined =
    level === 0
      ? undefined
      : coords
        ? {
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            zIndex: 1100 + level,
            visibility: 'visible',
          }
        : {
            // Первый кадр: оффскрин, чтобы измерить, потом useLayoutEffect поставит на место
            position: 'fixed',
            top: -9999,
            left: -9999,
            zIndex: 1100 + level,
            visibility: 'hidden',
          }

  return (
    <div
      ref={panelRef}
      className={cn(classes.panel, overlayClassName)}
      style={style}
      data-cascading-instance={instanceId}
      data-cascading-level={level}
      data-cascading-side={coords?.side ?? cascadeSide}
      {...(level === 0 ? { [rootDataAttr]: 'true' } : {})}
      onMouseEnter={onEnterPanel}
      onMouseLeave={() => onLeavePanel(level)}
      onMouseDown={(event) => {
        // Nested portal вне overlay antd — иначе mousedown = outside close.
        if (level > 0) {
          event.stopPropagation()
        }
      }}
      onPointerDown={(event) => {
        if (level > 0) {
          event.stopPropagation()
        }
      }}
    >
      {panelTitle != null && panelTitle !== '' ? (
        <div className={classes.panelTitle}>{panelTitle}</div>
      ) : null}
      {level === 0 && rootHeader != null ? (
        <div
          className={classes.rootHeader}
          onMouseDown={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
        >
          {rootHeader}
        </div>
      ) : null}
      <ul
        className={cn(classes.list, level === 0 && classes.rootList)}
        role="menu"
        style={
          level === 0 && rootListMaxHeight != null
            ? {
                maxHeight:
                  typeof rootListMaxHeight === 'number'
                    ? `${rootListMaxHeight}px`
                    : rootListMaxHeight,
                overflowY: 'auto',
              }
            : undefined
        }
        onScroll={level === 0 ? onRootScroll : undefined}
      >
        {items.length === 0 && level === 0 && rootEmptyContent != null ? (
          <li role="none" className={classes.emptyState}>
            {rootEmptyContent}
          </li>
        ) : null}
        {items.map((item) => {
          const hasChildren = Boolean(item.children?.length)
          const isActive = activeKey === item.key
          return (
            <li key={item.key} role="none">
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                className={cn(classes.item, {
                  [classes.itemActive]: isActive,
                  [classes.itemDisabled]: item.disabled,
                })}
                data-cascading-item-key={item.key}
                onMouseEnter={() => onHoverItem(level, item.key, hasChildren)}
                onPointerDown={(event: ReactPointerEvent<HTMLButtonElement>) => {
                  if (item.disabled || hasChildren) {
                    return
                  }
                  // Leaf: открываем на pointerdown — до outside-close antd.
                  event.preventDefault()
                  event.stopPropagation()
                  onPickLeaf(item, event.nativeEvent)
                }}
                onClick={(event: ReactMouseEvent<HTMLButtonElement>) => {
                  event.preventDefault()
                  event.stopPropagation()
                  if (item.disabled) {
                    return
                  }
                  if (hasChildren) {
                    onHoverItem(level, item.key, true)
                    return
                  }
                  // Keyboard / fallback, если pointerdown не открыл.
                  onPickLeaf(item, event.nativeEvent)
                }}
              >
                {hasChildren && cascadeSide === 'left' ? (
                  <ArrowIcon className={classes.itemArrow} />
                ) : null}
                <span className={classes.itemLabel}>{item.label}</span>
                {hasChildren && cascadeSide !== 'left' ? (
                  <ArrowIcon className={classes.itemArrow} />
                ) : null}
              </button>
            </li>
          )
        })}
        {level === 0 && rootListFooter != null ? (
          <li role="none" className={classes.listFooter}>
            {rootListFooter}
          </li>
        ) : null}
      </ul>
    </div>
  )
}

/**
 * Свой cascading dropdown (без antd SubMenu):
 * — hover открывает следующий уровень;
 * — увод курсора с панелей закрывает вложенные (корень click остаётся).
 */
const CascadingDropdown: FC<CascadingDropdownProps> = ({
  items,
  children,
  open: openProp,
  onOpenChange,
  placement = 'bottomRight',
  trigger = ['click'],
  disabled = false,
  destroyPopupOnHide = true,
  overlayClassName,
  rootDataAttr = DEFAULT_ROOT_ATTR,
  submenuLeaveDelayMs = 80,
  panelTitles,
  rootHeader,
  rootListFooter,
  onRootScroll,
  rootListMaxHeight,
  rootEmptyContent,
}) => {
  const reactId = useId()
  const instanceId = `cascading-dd-${reactId.replace(/:/g, '')}`
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  /** path[i] = key на уровне i; открывает панель уровня i+1. */
  const [path, setPath] = useState<string[]>([])
  const [itemRects, setItemRects] = useState<Record<string, DOMRect>>({})
  /** Сторона стрелок/следующего уровня по номеру панели. */
  const [sideByLevel, setSideByLevel] = useState<Record<number, CascadeSide>>({
    0: 'right',
  })
  const triggerRef = useRef<HTMLDivElement | null>(null)
  const leaveTimerRef = useRef<number | null>(null)
  /** Nested-панели в portal вне overlay antd — игнорим их «outside» close. */
  const ignoreOutsideCloseRef = useRef(false)
  const pickLockRef = useRef(false)
  const tree = useMemo(() => normalizeCascadingMenuItems(items), [items])

  const isControlled = openProp != null
  const open = isControlled ? Boolean(openProp) : uncontrolledOpen

  const clearLeaveTimer = () => {
    if (leaveTimerRef.current != null) {
      window.clearTimeout(leaveTimerRef.current)
      leaveTimerRef.current = null
    }
  }

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(next)
      }
      onOpenChange?.(next)
      if (!next) {
        setPath([])
        setItemRects({})
        setSideByLevel({ 0: 'right' })
        ignoreOutsideCloseRef.current = false
      }
    },
    [isControlled, onOpenChange],
  )

  const handleDropdownOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        // Nested-панели в portal: antd считает клик по ним outside.
        // Пока курсор над nested — не закрываем (leaf обработает сам).
        const nestedHovered = document.querySelector(
          `[data-cascading-instance="${instanceId}"][data-cascading-level]:not([data-cascading-level="0"]):hover`,
        )
        if (nestedHovered || ignoreOutsideCloseRef.current) {
          ignoreOutsideCloseRef.current = false
          return
        }
      }
      setOpen(next)
    },
    [instanceId, setOpen],
  )

  const isInsideInstance = useCallback(
    (node: Node | null) => {
      if (!node) return false
      if (triggerRef.current?.contains(node)) return true
      if (!(node instanceof Element)) return false
      return Boolean(
        node.closest(`[data-cascading-instance="${instanceId}"]`),
      )
    },
    [instanceId],
  )

  useLayoutEffect(() => {
    if (!open || disabled) return

    const onPointerDownCapture = (event: PointerEvent) => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (isInsideInstance(target)) {
        const el =
          target instanceof Element ? target : target.parentElement
        const panel = el?.closest(
          `[data-cascading-instance="${instanceId}"]`,
        )
        const level = Number(
          panel?.getAttribute('data-cascading-level') ?? -1,
        )
        // Клик по nested portal: antd Dropdown решит, что это outside —
        // не даём ему закрыть меню до onClick по пункту.
        if (level > 0) {
          ignoreOutsideCloseRef.current = true
        }
        return
      }
      setOpen(false)
    }

    // useLayoutEffect + capture: желательно раньше outside-handler antd.
    document.addEventListener('pointerdown', onPointerDownCapture, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDownCapture, true)
    }
  }, [disabled, instanceId, isInsideInstance, open, setOpen])

  useEffect(() => {
    if (!open) {
      setPath([])
      setItemRects({})
      setSideByLevel({ 0: 'right' })
    }
  }, [open])

  useEffect(() => () => clearLeaveTimer(), [])

  const findBranch = useCallback(
    (keys: string[]): CascadingMenuItem[] => {
      let current = tree
      for (let i = 0; i < keys.length; i += 1) {
        const match = current.find((item) => item.key === keys[i])
        if (!match?.children?.length) {
          return []
        }
        current = match.children
      }
      return current
    },
    [tree],
  )

  const trimRectsToLevel = (prev: Record<string, DOMRect>, maxLevel: number) => {
    const next: Record<string, DOMRect> = {}
    Object.keys(prev).forEach((key) => {
      const keyLevel = Number(key.split(':')[0])
      if (keyLevel < maxLevel) {
        next[key] = prev[key]
      }
    })
    return next
  }

  const onEnterPanel = useCallback(() => {
    clearLeaveTimer()
  }, [])

  const onPlacementResolved = useCallback((level: number, side: CascadeSide) => {
    setSideByLevel((prev) => {
      if (prev[level] === side && prev[level - 1] === side) {
        return prev
      }
      return {
        ...prev,
        // стрелки родителя указывают, куда открылся этот уровень
        ...(level > 0 ? { [level - 1]: side } : {}),
        // по умолчанию следующий уровень в ту же сторону
        [level]: side,
      }
    })
  }, [])

  const onHoverItem = useCallback(
    (level: number, itemKey: string, hasChildren: boolean) => {
      clearLeaveTimer()
      const button = document.querySelector(
        `[data-cascading-instance="${instanceId}"][data-cascading-level="${level}"] [data-cascading-item-key="${CSS.escape(itemKey)}"]`,
      )
      const rect = button?.getBoundingClientRect()

      setPath((prev) => {
        const next = prev.slice(0, level)
        if (hasChildren) {
          next.push(itemKey)
        }
        return next
      })

      if (hasChildren && rect) {
        setItemRects((prev) => ({
          ...trimRectsToLevel(prev, level),
          [`${level}:${itemKey}`]: rect,
        }))
      } else {
        setItemRects((prev) => trimRectsToLevel(prev, level))
      }
    },
    [instanceId],
  )

  const onLeavePanel = useCallback(
    (level: number) => {
      clearLeaveTimer()
      leaveTimerRef.current = window.setTimeout(() => {
        const hovered = document.querySelector(
          `[data-cascading-instance="${instanceId}"]:hover`,
        ) as HTMLElement | null

        if (hovered) {
          const hoveredLevel = Number(
            hovered.getAttribute('data-cascading-level') ?? -1,
          )
          if (hoveredLevel > level) {
            return
          }
          if (hoveredLevel >= 0 && hoveredLevel < level) {
            setPath((prev) => prev.slice(0, hoveredLevel))
            setItemRects((prev) => trimRectsToLevel(prev, hoveredLevel))
            return
          }
          if (hoveredLevel === level) {
            return
          }
        }

        const keep = Math.max(0, level - 1)
        setPath((prev) => prev.slice(0, keep))
        setItemRects((prev) => trimRectsToLevel(prev, keep))
      }, submenuLeaveDelayMs)
    },
    [instanceId, submenuLeaveDelayMs],
  )

  const onPickLeaf = useCallback(
    (item: CascadingMenuItem, domEvent: Event) => {
      if (pickLockRef.current) {
        return
      }
      pickLockRef.current = true
      item.onClick?.(domEvent)
      setOpen(false)
      window.setTimeout(() => {
        pickLockRef.current = false
      }, 0)
    },
    [setOpen],
  )

  const nestedPanels = useMemo(() => {
    const panels: ReactNode[] = []
    for (let level = 0; level < path.length; level += 1) {
      const branchKeys = path.slice(0, level + 1)
      const branchItems = findBranch(branchKeys)
      if (!branchItems.length) {
        break
      }
      const openKey = path[level]
      const rect = itemRects[`${level}:${openKey}`] ?? null
      panels.push(
        <CascadingPanel
          key={`panel-${level + 1}-${openKey}`}
          instanceId={instanceId}
          level={level + 1}
          items={branchItems}
          path={path}
          anchorRect={rect}
          cascadeSide={sideByLevel[level + 1] ?? sideByLevel[level] ?? 'right'}
          rootDataAttr={rootDataAttr}
          overlayClassName={overlayClassName}
          panelTitle={panelTitles?.[level + 1]}
          onHoverItem={onHoverItem}
          onLeavePanel={onLeavePanel}
          onEnterPanel={onEnterPanel}
          onPickLeaf={onPickLeaf}
          onPlacementResolved={onPlacementResolved}
        />,
      )
    }
    return panels
  }, [
    findBranch,
    instanceId,
    itemRects,
    onEnterPanel,
    onHoverItem,
    onLeavePanel,
    onPickLeaf,
    onPlacementResolved,
    overlayClassName,
    panelTitles,
    path,
    rootDataAttr,
    sideByLevel,
  ])

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    event.stopPropagation()
    setOpen(!open)
  }

  return (
    <>
      <Dropdown
        open={open}
        onOpenChange={handleDropdownOpenChange}
        disabled={disabled}
        trigger={trigger}
        placement={placement}
        destroyPopupOnHide={destroyPopupOnHide}
        overlayClassName={cn(classes.rootOverlay, overlayClassName)}
        dropdownRender={() => (
          <CascadingPanel
            instanceId={instanceId}
            level={0}
            items={tree}
            path={path}
            anchorRect={null}
            cascadeSide={sideByLevel[0] ?? 'right'}
            rootDataAttr={rootDataAttr}
            overlayClassName={overlayClassName}
            panelTitle={panelTitles?.[0]}
            rootHeader={rootHeader}
            rootListFooter={rootListFooter}
            onRootScroll={onRootScroll}
            rootListMaxHeight={rootListMaxHeight}
            rootEmptyContent={rootEmptyContent}
            onHoverItem={onHoverItem}
            onLeavePanel={onLeavePanel}
            onEnterPanel={onEnterPanel}
            onPickLeaf={onPickLeaf}
            onPlacementResolved={onPlacementResolved}
          />
        )}
      >
        <div
          ref={triggerRef}
          className={classes.trigger}
          data-cascading-instance={instanceId}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={onTriggerKeyDown}
        >
          {children}
        </div>
      </Dropdown>
      {open && typeof document !== 'undefined'
        ? createPortal(nestedPanels, document.body)
        : null}
    </>
  )
}

export default CascadingDropdown
